
import { mapActions, mapMutations, mapGetters, mapState } from 'vuex'

import ProductCardMixin from '@made-people/centra-storyblok-nuxt-shared/lib/mixins/ProductCardMixin'
import Collection from '~/components/collection/Collection'
import AlgoliaRecommendController from '~/components/algolia/AlgoliaRecommendController'
import Scale from '~/components/scale/Scale'
import TechnicalFeaturesAccordion from '~/components/technical-features/TechnicalFeaturesAccordion'
import FeatureToggle from '~/components/feature-toggle/FeatureToggle'
import ProductDescription from '~/components/product-details/ProductDescription'
import ProductPrice from '~/components/product-details/ProductPrice'
import ReturnIcon from '~/assets/icons/hundred-washes-return-icon.svg'
import SizeGuideIcon from '~/assets/icons/size-guide-link.svg'
import CustomisationBanner from '~/components/comfort-suite/customisation/CustomisationBanner.vue'
import StylePicker from '~/components/comfort-suite/style-picker/StylePicker.vue'
import EditIcon from '~/assets/icons/edit.svg'
import ProductAttributes from '~/components/product-details/ProductAttributes'
import MatchingProductsPdp from '~/components/matching-product-pdp/MatchingProductsPdp'

export default {
  name: 'ProductDetails',
  components: {
    AlgoliaRecommendController,
    TechnicalFeaturesAccordion,
    Collection,
    Scale,
    FeatureToggle,
    ChevronIcon: () => import('~/assets/icons/chevron.svg'),
    ProductDescription,
    ProductPrice,
    ReturnIcon,
    SizeGuideIcon,
    StylePicker,
    CustomisationBanner,
    EditIcon,
    ProductAttributes,
    MatchingProductsPdp
  },
  mixins: [ProductCardMixin],
  props: {
    product: {
      type: Object,
      required: true,
    }
  },
  data () {
    return {
      yotpoapi: '',
      domain: '',
      domainwithcountry: '',
      selectedSwatch: 1,
      selectedSize: null,
      loading: false,
      truncated: true,
      settings: {
        dots: false,
        arrows: false,
        draggable: false,
        speed: 200,
        slidesToShow: 1,
        slidesToScroll: 1,
        initialSlide: 0,
        swipeToSlide: true,
        infinite: true
      },
      discontinuedAndOutOfStock: false,
      algoliaRecommendControllerHitsCount: 0,
      discountBadges: [],
      hasQueryParams: false,
    }
  },
  head () {
    return {
      bodyAttrs: {
        class: 'product-page'
      },
    }
  },
  computed: {
    ...mapGetters({
      currentMarketId: 'frontend/currentMarketId',
      market: 'frontend/market',
      generalSettings: 'storyblok/settings',
      washingInstructions: 'storyblok/washingInstructions',
      cart: 'centra-cart/cart',
      currentLanguageCode: 'frontend/currentLanguageCode',
      getWashingInstructions: 'missmary-storyblok/getWashingInstructions',
      getYotpoReviews: 'yotpo/getProductReviews',
      cartPopupOpenMobile: 'ui/cartPopupOpenMobile',
      cartPopupOpenDesktop: 'ui/cartPopupOpenDesktop',
      countrySpecificInformation: 'storyblok/getCountrySpecificInformation',
      getGbFeatureValue: 'growthbook/getGbFeatureValue',
    }),
    ...mapState({
      selectedProductSize: state => state.ui.selectedProductSize,
      loadingYotpoReviews: state => state.yotpo.loadingReviews,
      lastClickedQueryID: state => state.ui.lastClickedQueryID,
      miniCartOpen: state => state.ui.miniCartOpen,
      searchOpen: state => state.ui.searchOpen,
      algoliaSettings: state => state.algolia.settings,
    }),
    customisationModalEnabled () {
      return this.getGbFeatureValue('comfort-suite:customisation-modal', false)
    },
    imageProxyUrl () { return this.$config.imageProxyUrl },
    algoliaRecommendColumns () {
      if (this.algoliaRecommendControllerHitsCount > 0 && this.algoliaRecommendControllerHitsCount < 2) {
        return '2'
      }
      return this.algoliaRecommendControllerHitsCount?.toString() ?? '4'
    },
    algoliaRecommendType () {
      return this.algoliaSettings?.content?.pdpRecommendationType ?? ''
    },
    newRelatedProducts () {
      if (this.GBNewSuggestedProductList) {
        return this.product.relatedPDPProducts && this.product.relatedPDPProducts.length
          ? this.product.relatedPDPProducts
          : (this.product.relatedProducts && this.product.relatedProducts.length ? this.product.relatedProducts : [])
      } else {
        return this.product.relatedProducts && this.product.relatedProducts.length ? this.product.relatedProducts : []
      }
    },
    technicalFeaturesAccordionBlockContainer () {
      return this.generalSettings?.technicalFeaturesAccordionBlockContainer
    },
    washingInstructions () {
      return this.getWashingInstructions(this.currentLanguageCode, this.currentMarketId)?.data
    },
    swatches () {
      return this.removeSwatchDuplicates(this.product?.swatchProducts || [])
    },
    collection () {
      if (this.product.product_type !== 'Bra') {
        return []
      }
      const collectionUrl = this.$u('bra')
      const collectionName = this.$t('Product_Type_Bra')

      const collection = [
        {
          position: 1,
          name: collectionName,
          url: collectionUrl
        }
      ]

      const wiringTypeName = this.product.wiring
      const wiringUrl = this.product.isWired === true ? this.$u('underwired-bra') : this.$u('non-wired-bra')

      collection.push({
        position: 2,
        name: wiringTypeName,
        url: wiringUrl
      })
      const paddingTypeName = this.product.padding
      const paddingUrl = this.product.isPadded === true ? this.$u('padded-bras') : this.$u('unpadded-bras')

      collection.push({
        position: 3,
        name: paddingTypeName,
        url: paddingUrl
      })

      const mouldingCupTypeName = this.product.cup_type
      const mouldingCupTypeNameOptions = ['Formstøbt', 'Moulded', 'Muotoonpuristettu', 'Moulé', 'Voorgevormd', 'Formstøpt', 'Moldada', 'Moldeado', 'Formpressad', 'Geformt']
      let mouldingUrl
      if (mouldingCupTypeNameOptions.includes(mouldingCupTypeName)) {
        mouldingUrl = this.$u('moulded-bra')
      } else {
        mouldingUrl = this.$u('non-moulded-bra')
      }

      collection.push({
        position: 4,
        name: mouldingCupTypeName,
        url: mouldingUrl
      })

      const braTypeUrlMapping = {
        'T-shirt bra': 't-shirt-bra',
        'Sports bra': 'sport-bra',
        'Post-Mastectomy Bra': 'mastectomy-bra',
        'Mastectomy Bra': 'mastectomy-bra',
        'Activity Bra': 'sport-bra'
      }

      const braTypeEn = this.product.bra_type_en
      if (braTypeEn in braTypeUrlMapping) {
        collection.push({
          position: 5,
          name: this.product.bra_type,
          url: this.$u(braTypeUrlMapping[braTypeEn])
        })
      }

      const braColor = this.product.color_swatch.description + ' ' + this.$t('Product_Type_Bra')
      const braColorUrlHelper = this.product.color_swatch.description.replace(/ /g, '---')
      const braColorUrl = this.$u(`bra?color=${braColorUrlHelper}`)

      collection.push({
        position: 6,
        name: braColor,
        url: braColorUrl
      })
      return collection
    },
    cupShapeSlider () {
      if (this.product.product_type === 'Bra') {
        const cupShape = this.product.cup_shape_default
        if (cupShape === null || cupShape === undefined) {
          return null
        }
        const value = parseInt(cupShape)

        return {
          scaleName: 'cupShape',
          label: this.$t('filter-custom_fields.cup_shape'),
          min: 0,
          max: 5,
          step: 1,
          value,
          marks: 5,
          markLabel: [this.$t('scale_cup_shape_pointy'), ' ', this.$t('scale_cup_shape_slightly_pointy'), ' ', this.$t('scale_cup_shape_round')]
        }
      }
      return null
    },
    supportSlider () {
      if (this.product.product_type === 'Bra') {
        const support = this.product.support_default
        if (support === null || support === undefined) {
          return null
        }
        const value = parseInt(support)

        return {
          scaleName: 'Support',
          label: this.$t('filter-support'),
          min: 0,
          max: 5,
          step: 1,
          value,
          marks: [null, null, null, null, null],
          markLabel: [this.$t('scale_support_low'), '  ', this.$t('scale_support_medium'), '  ', this.$t('scale_support_high')]
        }
      }

      return null
    },
    // liftSlider () {
    //   if (this.product.product_type === 'Bra') {
    //     let value
    //     switch (this.product.lift_default) {
    //       case '1':
    //         value = 6
    //         break
    //       case '2':
    //         value = 5
    //         break
    //       case '3':
    //         value = 4
    //         break
    //       case '4':
    //         value = 3
    //         break
    //       case '5':
    //         value = 2
    //         break
    //       default:
    //         return null
    //     }

    //     return {
    //       scaleName: 'Lift',
    //       label: this.$t('filter-lift'),
    //       min: 0,
    //       max: 7,
    //       step: 1,
    //       value,
    //       marks: [null, null, null, null, null, null, null],
    //       markLabel: [' ', this.$t('scale_support_low'), ' ', this.$t('scale_support_medium'), ' ', this.$t('scale_support_high'), ' ']
    //     }
    //   }
    //   return null
    // },
    breadCrumbs () {
      let url = ''
      let name = ''
      if (this.product.product_type === 'Bra') {
        url = this.$u('bra')
        name = this.$t('Product_Type_Bra')
      } else if (this.product.product_type?.includes('Panties')) {
        url = this.$u('panties')
        name = this.$t('product_Type_Panties')
      } else if (this.product.product_type === 'Swimwear') {
        url = this.$u('swimwear')
        name = this.$t('Product_Type_Swimwear')
      } else if (this.product.product_type === 'Girdles') {
        url = this.$u('panties')
        name = this.$t('Product_Type_Panties')
      }
      const breadcrumbs = [
        {
          position: 1,
          name: this.$t('Global_Shop'),
          url: this.$u('')
        }
      ]

      if (name !== '' && url !== '') {
        breadcrumbs.push({
          position: 2,
          name,
          url
        })
      }
      let productNameBreadcrumb = this.product.name

      if (this.product.product_type === 'Bra') {
        const mouldingCupTypeName = this.product.cup_type
        const mouldingCupTypeNameOptions = ['Formstøbt', 'Moulded', 'Muotoonpuristettu', 'Moulé', 'Voorgevormd', 'Formstøpt', 'Moldada', 'Moldeado', 'Formpressad', 'Geformt']
        let mouldingUrl
        if (mouldingCupTypeNameOptions.includes(mouldingCupTypeName)) {
          mouldingUrl = this.$u('moulded-bra')
        } else {
          mouldingUrl = this.$u('non-moulded-bra')
        }

        const wiringTypeName = this.product.wiring
        let wiringUrl
        if (this.product.isWired === true) {
          wiringUrl = this.$u('underwired-bra')
        } else {
          wiringUrl = this.$u('non-wired-bra')
        }

        const stringsToReplace = ['Soutien-gorge', 'pour t-shirt', 'fermé devant', 'pour', 'de sport', 'Rintaliivit', 'T-paitarintaliivit', 'rintaliivit', 'etukiinnityksellä', 'Urheilu']

        stringsToReplace.forEach((str) => {
          if (productNameBreadcrumb.includes(str)) {
            productNameBreadcrumb = productNameBreadcrumb.replace(str, '')
          }
        })
        if (this.$store.getters['frontend/currentLanguageCode'] !== 'fi') {
          breadcrumbs.push({
            position: 3,
            name: mouldingCupTypeName,
            url: mouldingUrl
          })
        }

        breadcrumbs.push({
          position: 4,
          name: wiringTypeName,
          url: wiringUrl
        })
      }
      breadcrumbs.push({
        position: 5,
        name: productNameBreadcrumb,
        url: this.$u(this.product.url)
      })
      return breadcrumbs
    },
    showMoreBtnLabel () {
      return this.truncated ? this.$t('Product_Details_Show_More') : this.$t('Product_Details_Show_Less')
    },
    isInWishlist () {
      return this.hasProductID(this.product.product)
    },
    videoPoster () {
      return {
        filename: this.product?.videoImageUrl,
        alt: this.product?.name
      }
    },
    sizeGuideBlock () {
      return {
        image: this.generalSettings?.pdpSizeGuideImage?.filename,
        text: this.generalSettings?.pdpSizeGuideText,
        color: this.generalSettings?.pdpSizeGuideTextColor
      }
    },
    bracrobaticsEnabled () {
      return !!(this.generalSettings?.pdpBracrobaticsVisibility === '1' && this.bracrobaticsBlock.image)
    },
    bracrobaticsBlock () {
      return {
        link: this.generalSettings?.pdpBracrobaticsLink,
        image: this.generalSettings?.pdpBracrobaticsImage?.filename,
        text: this.generalSettings?.pdpBracrobaticsText,
        color: this.generalSettings?.pdpBracrobaticsTextColor
      }
    },
    washingInstructionsBlock () {
      return {
        image: this.generalSettings?.pdpWashingInstructionsImage?.filename,
        mobileText: this.generalSettings?.pdpWashingInstructionsTextMobile,
        color: this.generalSettings?.pdpWashingInstructionsTextColor,
        desktopText: this.generalSettings?.pdpWashingInstructionsTextDesktop,
      }
    },
    hundredWashes () {
      const washingWarrantyActiveProductSkus = this.generalSettings?.washingsWarrantyActiveProductSkus?.split(',') || []
      if (this.product.product_type === 'Bra' || washingWarrantyActiveProductSkus.includes(this.product.productSku)) {
        if (this.$u(this.countrySpecificInformation?.content?.WashesLinkUrl) &&
          this.countrySpecificInformation?.content?.WashesIcon) {
          return {
            link: this.$u(this.countrySpecificInformation?.content?.WashesLinkUrl),
            icon: this.countrySpecificInformation?.content?.WashesIcon,
            show: true
          }
        }
      }
      return { show: false }
    },
    washingsWarrantyTopPage () {
      return this.getGbFeatureValue('pdp:washings-warranty-top-position-page')
    },
    GBWiringToggle () {
      return this.getGbFeatureValue('pdp:wiring-toggle')
    },
    GBClosureToggle () {
      return this.getGbFeatureValue('pdp:closure-toggle')
    },
    GBLaceToggle () {
      return this.getGbFeatureValue('pdp:lace-toggle')
    },
    GBShapingToggle () {
      return this.getGbFeatureValue('pdp:shaping-toggle')
    },
    GBSideWingToggle () {
      return this.getGbFeatureValue('pdp:side-wing-toggle')
    },
    GBTechnicalFeatureAccordion () {
      return this.getGbFeatureValue('pdp:technical-features-accordion')
    },
    GBVideoFirst () {
      return this.getGbFeatureValue('pdp:video-first-in-image-carousel')
    },
    GBShortDescription () {
      return this.getGbFeatureValue('pdp:short-description')
    },
    GBPdpOldDesign () {
      return this.getGbFeatureValue('pdp:old-design-active')
    },
    GBCategoryTags () {
      return this.getGbFeatureValue('category-tags-on-pdp')
    },
    GBBraScale () {
      return this.getGbFeatureValue('pdp:bra-scale-placement')
    },
    GBNewSuggestedProductList () {
      return this.getGbFeatureValue('pdp:new-suggested-product-list')
    },
    GBMatchingProductLayout () {
      return this.getGbFeatureValue('pdp:matching-product-layout')
    },
    limitedMatchingProducts () {
      return this.newRelatedProducts.slice(0, 3)
    },
    selectedProduct () {
      let product = ''
      if (this.selectedProductSize) {
        if (this.product.product === this.selectedProductSize?.id) {
          product = this.selectedProductSize
        }
      }
      return product
    },
    hasTwoSizes () {
      return !!this.product.sizeOptions[0].label.match(/.+x[A-G]/)
    },
    checkSizeQty () {
      return this.hasTwoSizes ? 2 : 1
    },
    isAvailable () {
      if (this.selectedProductSize) {
        if (this.selectedProductSize.product?.quantity > 0) {
          return true
        } else {
          return false
        }
      } else {
        return false
      }
    },
    yotpoReviews () {
      return this.getYotpoReviews(this.product.productSku)
    },
    hasYotpoReviews () {
      return !(this.yotpoReviews === null || this.yotpoReviews?.reviews?.length === 0)
    },
    showLowestPriceInLast30Days () {
      return this.market.defaultCountry === 'SE' && this.product.discounted && this.product.lowestPriceLast30DaysAsNumber < this.product.priceBeforeDiscountAsNumber
    },
    swatchDescription () {
      if (this.product.color_swatch && this.product.color_swatch.description) {
        return this.product.color_swatch.description
      }
      return undefined
    },
    localizedBadges () {
      const badgesColorAndText = []

      if (this.product?.badge?.[this.currentLanguageCode]) {
        for (const [key, value] of Object.entries(this.product?.badge?.[this.currentLanguageCode])) {
          badgesColorAndText.push({
            color: key,
            text: value
          })
        }
      }

      if (this.discountBadges.length > 0) {
        badgesColorAndText.unshift(...this.discountBadges)
      }
      return badgesColorAndText
    },
    productImagesAndVideo () {
      const images = this.product.media
      const video = this.product.variantProductVideoCarousel
      if (video) {
        return [...images, video]
      }
      return images
    },
    productVideoFirstAndImages () {
      const images = this.product.media
      const video = this.product.variantProductVideoCarousel
      if (video) {
        return [video, ...images]
      }
      return images
    },
    productHighlights () {
      const highlightArray = this.product?.productHighlight?.split(';')
      return highlightArray?.filter?.(Boolean)?.map(highlight => highlight.trim())
    }
  },
  created () {
    this.selectedSize = this.product.isOnesize ? this.product.sizeOptions[0] : null
    this.setInitialValue(this.selectedProductSize)
    this.setProduct(Object.assign(
      {
        ...(this.product ?? {}),
      },
      this.product?.sizeOptions
        ? {
            sizeOptions: this.product.sizeOptions.map((option) => {
              return {
                ...option,
                addToCart: undefined,
              }
            })
          }
        : {}
    ))

    this.loadYotpoQuestions(this.product.productSku)
  },
  mounted () {
    this.discountBadges = this.getDiscountBadges(this.product).map((badge) => {
      return {
        color: badge.color,
        text: badge.text
      }
    })
    this.checkQueryParams()
  },
  methods: {
    ...mapMutations({
      setProduct: 'ui/product'
    }),
    ...mapActions({
      loadYotpoQuestions: 'yotpo/loadProductQuestions',
    }),

    handleClickBuyButtonMobile () {
      this.setInitialValue(this.selectedProductSize)
      if (this.selectedProductSize && this.isAvailable) {
        this.addToCart(this.selectedProductSize, { mobile: true })
      } else {
        this.openSizeSelectorMobile()
      }
    },
    handleClickBuyButtonDesktop () {
      this.setInitialValue(this.selectedProductSize)
      if (this.selectedProductSize && this.isAvailable) {
        this.addToCart(this.selectedProductSize, { mobile: false })
      } else {
        this.openSizeSelectorDesktop()
      }
    },
    badgeBackgroundColor (badge) {
      return this.generalSettings?.pdpBadgeColors?.[0]?.['color_' + badge.color]?.color ?? '#383838'
    },
    badgeTextColor (badge) {
      return this.generalSettings?.pdpTextColorForBadge?.[0]?.['color_' + badge.color]?.color ?? '#FFFFFF'
    },
    relatedProductsBadges (relatedProduct) {
      const badgesColorAndText = []
      if (relatedProduct.badges?.[this.currentLanguageCode]) {
        for (const [key, value] of Object.entries(relatedProduct.badges[this.currentLanguageCode])) {
          badgesColorAndText.push({
            color: key,
            text: value
          })
        }
      }
      const relatedProductsDiscountBadges = this.getDiscountBadges(relatedProduct)
      if (relatedProductsDiscountBadges.length > 0) {
        badgesColorAndText.unshift(...relatedProductsDiscountBadges)
      }
      return badgesColorAndText
    },
    getDiscountBadges (product) {
      const badges = []
      const priceAsNumber = product?.priceAsNumber || -1
      const priceBeforeDiscountAsNumber = product?.priceBeforeDiscountAsNumber || -1
      if (priceAsNumber !== -1 && priceBeforeDiscountAsNumber !== -1) {
        if (priceBeforeDiscountAsNumber > priceAsNumber) {
          const discountPercentage = Math.round((priceBeforeDiscountAsNumber - priceAsNumber) / priceBeforeDiscountAsNumber * 100)
          badges.push({
            text: '-' + discountPercentage.toString() + '%',
            color: 'discount'
          })
        }
      }
      return badges
    },
    async addToCart (selectedSize, args = {}) {
      const defaults = {
        mobile: false
      }
      const { mobile } = { ...defaults, ...args }

      this.loading = true
      this.$store.dispatch('checkout/startLoadingPaypalButton')
      this.$store.dispatch('checkout/startLoadingStripeButton')
      try {
        await selectedSize?.product?.addToCart()
      } finally {
        this.loading = false
        let cartItem = null
        this.cart?.items.forEach(function (item) {
          if (item.size === selectedSize.product.label) {
            cartItem = item
          }
        })
        if (cartItem) {
          if (mobile) {
            this.$store.dispatch('ui/showCartPopupMobile', {
              addedProduct: cartItem,
              addedPrimarySize: selectedSize.primarySize,
              addedSecondarySize: selectedSize.secondarySize
            })
          } else {
            this.$store.dispatch('ui/showCartPopupDesktop', {
              addedProduct: cartItem,
            })
          }
        }
      }
    },
    openSizeSelectorMobile () {
      this.setInitialValue(this.selectedProductSize)
      this.$store.dispatch(
        'ui/showSizeSelectorMobile',
        {
          productID: this.product.product,
          discontinued: this.product.discontinued,
          productItems: this.product.items
        }
      )
    },
    openSizeSelectorDesktop () {
      this.setInitialValue(this.selectedProductSize)
      this.$store.dispatch(
        'ui/showSizeSelectorDesktop',
        {
          productID: this.product.product,
          discontinued: this.product.discontinued,
          productItems: this.product.items
        }
      )
    },
    sizeGuide () {
      this.$store.dispatch('ui/showSizeGuide', { productType: this.product.product_type })
    },
    openWashingInstructions () {
      this.$store.dispatch('ui/showWashingInstructions')
    },
    toggleAddToWishlist () {
      if (this.isInWishlist) {
        this.removeFromWishlist()
      } else {
        this.addToWishlist()
      }
    },
    removeFromWishlist () {
      this.$store.dispatch('wishlist/removeProductID', this.product.product)
    },
    addToWishlist () {
      this.$store.dispatch('wishlist/addProductID', this.product.product)
    },
    goToElement (refName) {
      const element = this.$refs[refName]
      const top = element.offsetTop - 80
      window.scrollTo({
        top,
        left: 0,
        behavior: 'smooth'
      })
    },
    matchingProductImages (images) {
      return images.slice(0, 2)
    },
    matchingProductPrices (product) {
      return {
        price: product.price,
        priceBeforeDiscount: product.price !== product.priceBeforeDiscount ? product.priceBeforeDiscount : null,
        isDiscounted: product.price !== product.priceBeforeDiscount
      }
    },
    setInitialValue (selectedProductSize) {
      if (this.product?.product_type === 'Giftcard') {
        this.setGiftcardInitialValue()
      } else if (selectedProductSize && this.product?.product_type !== 'Giftcard') {
        if (selectedProductSize.type === this.product.product_type && this.checkSizeQty === selectedProductSize.size) {
          const product = this.hasTwoSizes
            ? this.findSizeObject(selectedProductSize.secondarySize + 'x' + selectedProductSize.primarySize)
            : this.findSizeObject(selectedProductSize.primarySize)
          const addedProduct = {
            id: this.product.product,
            label: selectedProductSize.label,
            product,
            primarySize: selectedProductSize.primarySize,
            secondarySize: selectedProductSize.secondarySize,
            type: this.product.product_type,
            color: this.product.color_swatch.description,
            size: this.checkSizeQty
          }
          this.$store.dispatch('ui/selectedProductSize', addedProduct)
        } else {
          this.$store.dispatch('ui/selectedProductSize', false)
        }
      }
    },
    setGiftcardInitialValue () {
      const sizeOptions = this.product?.sizeOptions?.[0]
      const product = this.findSizeObject(sizeOptions?.label)
      const addedProduct = {
        id: this.product.product,
        label: sizeOptions?.label,
        product,
        primarySize: sizeOptions?.label,
        secondarySize: undefined,
        type: this.product.product_type,
        color: this.product.color_swatch.description,
        size: this.checkSizeQty
      }
      this.$store.dispatch('ui/selectedProductSize', addedProduct)
    },
    findSizeObject (sizeLabel) {
      return this.product.sizeOptions.find(sizeOption => sizeOption.label === sizeLabel)
    },
    handleClickStars () {
      if (this.$refs?.reviewsContainer) {
        this.$refs.reviewsContainer.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
      this.removeQueryParams()
    },
    removeSwatchDuplicates (swatches) {
      return swatches.filter((i, index, self) =>
        index === self.findIndex(t => (
          t.product === i.product
        ))
      )
    },
    convertToProxy (image) {
      return this.$imgproxy.transform(image, 'rs:fit:1000/g:nowe/q:70')
    },
    checkQueryParams () {
      const queryParams = new URLSearchParams(window.location.search)
      this.hasQueryParams = queryParams.has('showReviews') && queryParams.get('showReviews') === 'true'

      if (this.hasQueryParams) {
        setTimeout(() => {
          this.handleClickStars()
        }, 500)
      }
    },
    removeQueryParams () {
      if (this.hasQueryParams) {
        const urlWithoutQueryParam = window.location.href.replace(/[?&]showReviews=true/, '')
        window.history.replaceState({}, document.title, urlWithoutQueryParam)
      }
    },
    customisationActive () {
      return this.generalSettings.style_picker.some(style =>
        this.product.categories.some(category =>
          style.category_page === category.uri
        )
      )
    },
    isCustomizable () {
      if (this.product.relatedLaceProducts.length > 0 || this.product.relatedSideWingProducts.length > 0 || this.product.relatedWiringProducts.length > 0) {
        return true
      }
    },
  }
}
